import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { RefreshCw, Shield } from 'lucide-react';
import { adminAPI } from '../services/api';
import { formatErrorDetail } from '../utils/errorFormat';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Actions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const Button = styled.button`
  padding: 0.6rem 1.1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  border: none;
  background: ${props => (props.$variant === 'secondary' ? props.theme.colors.surface : props.theme.colors.primary)};
  color: ${props => (props.$variant === 'secondary' ? props.theme.colors.text : '#fff')};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid ${props => (props.$variant === 'secondary' ? props.theme.colors.border : 'transparent')};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${props => props.theme.spacing.lg};
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  gap: 0.3rem;
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  font-size: 1rem;
`;

const Select = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  font-size: 1rem;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.text};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const Th = styled.th`
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.9rem 1rem;
  background: ${props => props.theme.colors.background};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  font-size: 0.95rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => (props.$active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)')};
  color: ${props => (props.$active ? '#059669' : '#dc2626')};
`;

const InlineActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const AdminUsers = ({ currentAdmin, onCurrentAdminUpdate }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [createForm, setCreateForm] = useState({
    email: '',
    full_name: '',
    role: 'admin',
    password: '',
    confirmPassword: '',
    is_active: true,
  });

  const [editId, setEditId] = useState('');
  const [editForm, setEditForm] = useState({
    full_name: '',
    role: 'admin',
    is_active: true,
  });

  const [passwordForm, setPasswordForm] = useState({
    adminId: '',
    password: '',
    confirmPassword: '',
  });

  const selectedAdmin = useMemo(
    () => admins.find(admin => String(admin.id) === String(editId)),
    [admins, editId]
  );

  useEffect(() => {
    if (selectedAdmin) {
      setEditForm({
        full_name: selectedAdmin.full_name || '',
        role: selectedAdmin.role || 'admin',
        is_active: Boolean(selectedAdmin.is_active),
      });
    } else {
      setEditForm({
        full_name: '',
        role: 'admin',
        is_active: true,
      });
    }
  }, [selectedAdmin]);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getAdminUsers({ search: params.search ?? search });
      setAdmins(data.admins || data.items || []);
    } catch (error) {
      console.error('Failed to load admins', error);
      toast.error(formatErrorDetail(error, 'Не удалось загрузить администраторов'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!createForm.email || !createForm.password) {
      toast.error('Заполните email и пароль');
      return;
    }
    if (createForm.password !== createForm.confirmPassword) {
      toast.error('Пароли не совпадают');
      return;
    }

    try {
      await adminAPI.createAdminUser({
        email: createForm.email,
        password: createForm.password,
        full_name: createForm.full_name || null,
        role: createForm.role || 'admin',
        is_active: createForm.is_active,
      });
      toast.success('Администратор создан');
      setCreateForm({
        email: '',
        full_name: '',
        role: 'admin',
        password: '',
        confirmPassword: '',
        is_active: true,
      });
      fetchAdmins();
    } catch (error) {
      console.error('Failed to create admin', error);
      toast.error(formatErrorDetail(error, 'Не удалось создать администратора'));
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editId) {
      toast.error('Выберите администратора');
      return;
    }
    try {
      const { data } = await adminAPI.updateAdminUser(editId, {
        full_name: editForm.full_name,
        role: editForm.role,
        is_active: editForm.is_active,
      });
      toast.success('Данные обновлены');
      if (currentAdmin && Number(editId) === Number(currentAdmin.id)) {
        onCurrentAdminUpdate?.({ ...currentAdmin, ...data });
      }
      fetchAdmins();
    } catch (error) {
      console.error('Failed to update admin', error);
      toast.error(formatErrorDetail(error, 'Не удалось обновить данные'));
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    if (!passwordForm.adminId) {
      toast.error('Выберите администратора');
      return;
    }
    if (!passwordForm.password) {
      toast.error('Введите новый пароль');
      return;
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      toast.error('Пароли не совпадают');
      return;
    }
    try {
      await adminAPI.updateAdminPassword(passwordForm.adminId, {
        password: passwordForm.password,
      });
      toast.success('Пароль обновлён');
      setPasswordForm({
        adminId: '',
        password: '',
        confirmPassword: '',
      });
    } catch (error) {
      console.error('Failed to update password', error);
      toast.error(formatErrorDetail(error, 'Не удалось обновить пароль'));
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchAdmins({ search });
  };

  const toggleActive = async (admin) => {
    try {
      const { data } = await adminAPI.updateAdminUser(admin.id, { is_active: !admin.is_active });
      toast.success(admin.is_active ? 'Администратор деактивирован' : 'Администратор активирован');
      if (currentAdmin && Number(admin.id) === Number(currentAdmin.id)) {
        onCurrentAdminUpdate?.({ ...currentAdmin, ...data });
      }
      fetchAdmins();
    } catch (error) {
      console.error('Failed to toggle state', error);
      toast.error(formatErrorDetail(error, 'Не удалось изменить статус'));
    }
  };

  return (
    <PageContainer>
      <HeaderRow>
        <Title>
          <Shield size={28} />
          Администраторы
        </Title>
        <Actions>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
            <Input
              type="text"
              placeholder="Поиск по email или имени"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: '220px' }}
            />
            <Button type="submit" $variant="secondary">Искать</Button>
          </form>
          <Button onClick={() => fetchAdmins()} disabled={loading}>
            <RefreshCw size={16} />
            Обновить
          </Button>
        </Actions>
      </HeaderRow>

      <CardGrid>
        <Card>
          <CardTitle>Добавить администратора</CardTitle>
          <Form onSubmit={handleCreate}>
            <Label>
              Email
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Label>
            <Label>
              Имя
              <Input
                value={createForm.full_name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, full_name: e.target.value }))}
              />
            </Label>
            <Label>
              Роль
              <Select
                value={createForm.role}
                onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="admin">admin</option>
                <option value="viewer">viewer</option>
              </Select>
            </Label>
            <Label>
              Пароль
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                required
              />
            </Label>
            <Label>
              Повторите пароль
              <Input
                type="password"
                value={createForm.confirmPassword}
                onChange={(e) => setCreateForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
              />
            </Label>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={createForm.is_active}
                onChange={(e) => setCreateForm(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              Активен
            </CheckboxRow>
            <Button type="submit">Добавить</Button>
          </Form>
        </Card>

        <Card>
          <CardTitle>Редактировать администратора</CardTitle>
          <Form onSubmit={handleEditSubmit}>
            <Label>
              Выберите администратора
              <Select value={editId} onChange={(e) => setEditId(e.target.value)}>
                <option value="">—</option>
                {admins.map(admin => (
                  <option key={admin.id} value={admin.id}>
                    {admin.email}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Имя
              <Input
                value={editForm.full_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                disabled={!editId}
              />
            </Label>
            <Label>
              Роль
              <Select
                value={editForm.role}
                onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                disabled={!editId}
              >
                <option value="admin">admin</option>
                <option value="viewer">viewer</option>
              </Select>
            </Label>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={editForm.is_active}
                onChange={(e) => setEditForm(prev => ({ ...prev, is_active: e.target.checked }))}
                disabled={!editId}
              />
              Активен
            </CheckboxRow>
            <Button type="submit" disabled={!editId}>Сохранить</Button>
          </Form>
        </Card>

        <Card>
          <CardTitle>Сменить пароль</CardTitle>
          <Form onSubmit={handlePasswordChange}>
            <Label>
              Администратор
              <Select
                value={passwordForm.adminId}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, adminId: e.target.value }))}
              >
                <option value="">—</option>
                {admins.map(admin => (
                  <option key={admin.id} value={admin.id}>
                    {admin.email}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Новый пароль
              <Input
                type="password"
                value={passwordForm.password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, password: e.target.value }))}
              />
            </Label>
            <Label>
              Повторите пароль
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              />
            </Label>
            <Button type="submit">Изменить пароль</Button>
          </Form>
        </Card>
      </CardGrid>

      <Card>
        <CardTitle>Все администраторы</CardTitle>
        {admins.length === 0 ? (
          <EmptyState>Пока нет администраторов</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th>Имя</Th>
                <Th>Роль</Th>
                <Th>Статус</Th>
                <Th>Последний вход</Th>
                <Th>Действия</Th>
              </tr>
            </thead>
            <tbody>
              {admins.map(admin => (
                <tr key={admin.id}>
                  <Td>{admin.email}</Td>
                  <Td>{admin.full_name || '—'}</Td>
                  <Td>{admin.role}</Td>
                  <Td>
                    <StatusBadge $active={admin.is_active}>
                      {admin.is_active ? 'Активен' : 'Отключен'}
                    </StatusBadge>
                  </Td>
                  <Td>{admin.last_login_at ? new Date(admin.last_login_at).toLocaleString() : '—'}</Td>
                  <Td>
                    <InlineActions>
                      <Button
                        type="button"
                        $variant="secondary"
                        onClick={() => toggleActive(admin)}
                      >
                        {admin.is_active ? 'Деактивировать' : 'Активировать'}
                      </Button>
                      <Button
                        type="button"
                        $variant="secondary"
                        onClick={() => {
                          setPasswordForm({
                            adminId: admin.id,
                            password: '',
                            confirmPassword: '',
                          });
                          toast('Введите новый пароль в форме «Сменить пароль»', { icon: 'ℹ️' });
                        }}
                      >
                        Пароль
                      </Button>
                    </InlineActions>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </PageContainer>
  );
};

export default AdminUsers;

